import { useState } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  CircleSlash2Icon,
  DownloadIcon,
  Loader2Icon,
  PrinterIcon,
} from 'lucide-react';
import { useSnapshot } from 'valtio';

import CardCoupon from '../../components/pages/customer-portal/card-coupon.jsx';
import CardInvoice from '../../components/pages/customer-portal/card-invoice.jsx';
import PortalContainer from '../../components/pages/customer-portal/container.jsx';
import { CustomerAuthenticationWrapper } from '../../components/pages/customer-portal/customer-portal-authentication-wrapper.jsx';
import PortalSubHeading from '../../components/pages/customer-portal/heading-sub.jsx';
import PortalHeading from '../../components/pages/customer-portal/heading.jsx';
import CustomerCouponsSummary from '../../components/pages/customer-portal/printables/customer-coupons-summary.jsx';
import ContactsInfo from '../../components/contacts-info.jsx';
import Error from '../../components/feedback/error.jsx';
import Loading from '../../components/feedback/loading.jsx';
import { DataTableFooter } from '../../components/table/data-table-footer.jsx';
import { Button } from '../../components/ui/button.jsx';
import { DebouncedInput } from '../../components/ui/input-debounced.jsx';
import { msCatalystApi } from '../../services/api/ms-catalyst-api.js';
import { authState } from '../../services/state/auth-state.jsx';
import { downloadCouponsPdf } from '../../helpers/download-coupons-pdf.js';
import { print } from '../../helpers/print.js';
import { useQueryActiveCampaign } from '../../hooks/queries/use-active-campaign.jsx';
import useDeviceDetection from '../../hooks/use-device.jsx';
import { useMediaQuery } from '../../hooks/use-media-query.jsx';
import { useTable } from '../../hooks/use-table.jsx';
import { groupByKey } from '../../utils/arr.js';
import { INVOICES_STATES } from '../../utils/constants.js';
import { scrollToId } from '../../utils/scroll.js';

const TABLE_COLUMNS = [
  {
    id: 'ROWID',
    accessorKey: 'coupons.ROWID',
    cell: ({ row }) => (
      <CardCoupon
        key={row.id}
        coupon={row.original.coupons}
        invoice={row.original.invoices}
      />
    ),
  },
];

export default function CustomerPortalCouponsList() {
  const { user } = useSnapshot(authState);
  const [initRowsCount, setInitRowsCount] = useState(null);
  const device = useDeviceDetection();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const queryCampaign = useQueryActiveCampaign();
  const campaign = queryCampaign.data;

  const queryInvoices = useQuery({
    queryKey: ['invoices_customer_activeCampaign', campaign?.ROWID],
    queryFn: async () => {
      const { data: invoices } = await msCatalystApi.get(
        `customers/invoices/all?invoices.campaignId=${campaign.ROWID}`,
      );
      const groupedData = groupByKey(invoices.data, 'invoices.status');
      return groupedData;
    },
    placeholderData: keepPreviousData,
    enabled: !!campaign?.ROWID,
  });

  const {
    table,
    query: queryCoupons,
    filters,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['coupons_customer_activeCampaign', campaign?.ROWID],
      queryFn: async () => {
        const { data: customerCoupons } = await msCatalystApi.get(
          `customers/coupons/paginate?${filters}&coupons.campaignId=${campaign.ROWID}`,
        );
        if (!initRowsCount) setInitRowsCount(customerCoupons.data.rowCount);
        return customerCoupons.data;
      },
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      enabled: !!campaign?.ROWID,
    },
    {
      sorting: [
        {
          id: 'coupons.codeNumberInt',
          desc: false,
        },
      ],
      pagination: {
        pageIndex: 0,
        pageSize: 24,
      },
    },
  );

  const mutationCustomerCoupons = useMutation({
    mutationFn: async () => {
      const { data: coupons } = await msCatalystApi.get(
        `customers/coupons/all?order_by=CREATEDTIME,desc&coupons.campaignId=${campaign.ROWID}`,
      );

      return coupons.data;
    },
    onSuccess: (data) => {
      if (!data?.length) return;

      const campaignName = campaign.name;
      const campaignLegalPhrase = campaign.legalPhrase;

      if (device === 'Mobile' || device === 'Tablet') {
        downloadCouponsPdf({
          component: (
            <CustomerCouponsSummary
              customer={user.details}
              coupons={data.map((item) => item.coupons)}
              campaign={{
                name: campaignName,
                legalPhrase: campaignLegalPhrase,
              }}
            />
          ),
        });
      } else {
        print({
          component: (
            <CustomerCouponsSummary
              customer={user.details}
              coupons={data.map((item) => item.coupons)}
              campaign={{
                name: campaignName,
                legalPhrase: campaignLegalPhrase,
              }}
            />
          ),
        });
      }
    },
  });

  return (
    <CustomerAuthenticationWrapper>
      <PortalContainer>
        {queryCampaign.isLoading ? (
          <div className="flex h-[70dvh] items-center justify-center">
            <Loading message="A carregar" />
          </div>
        ) : queryCampaign.isError ? (
          <Error />
        ) : !campaign ? (
          <div className="flex flex-col items-center justify-center">
            <img
              src={
                isDesktop
                  ? './assets/no-active-campaign_1920X1080px.png'
                  : './assets/no-active-campaign_1080X1920px.png'
              }
              alt="Não existe nenhuma campanha ativa"
              className="rounded-[40px] object-contain"
            />

            <ContactsInfo />
          </div>
        ) : queryCoupons.isLoading ? (
          <div className="flex h-[70dvh] items-center justify-center">
            <Loading />
          </div>
        ) : queryCoupons.isError ? (
          <Error />
        ) : (
          <div className="w-full lg:grid lg:grid-cols-11">
            {/* left-side */}
            <div className="h-full w-full lg:col-span-3">
              <div className="flex flex-col lg:sticky lg:top-32">
                <PortalHeading>Cupões</PortalHeading>
                <div className="mt-8 flex w-full flex-col gap-[26px] lg:mt-12 lg:max-w-[330px] ">
                  <button
                    onClick={() => scrollToId(INVOICES_STATES.APPROVED)}
                    className="flex cursor-pointer items-center justify-between rounded-[12px] border border-portal-foreground p-3 text-portal-text"
                  >
                    <span>Cupões</span>
                    <span>( {initRowsCount || 0} )</span>
                  </button>
                  <button
                    onClick={() => scrollToId(INVOICES_STATES.PENDING)}
                    className="flex cursor-pointer items-center justify-between rounded-[12px] border border-portal-foreground p-3"
                  >
                    <span>Faturas pendentes</span>
                    <span className="text-amber-500">
                      ({' '}
                      {queryInvoices?.data?.[INVOICES_STATES.PENDING]?.length ||
                        0}{' '}
                      )
                    </span>
                  </button>
                  <button
                    onClick={() => scrollToId(INVOICES_STATES.REJECTED)}
                    className="flex cursor-pointer items-center justify-between rounded-[12px] border border-portal-foreground p-3"
                  >
                    <span>Faturas rejeitadas</span>
                    <span className="text-portal-destructive">
                      ({' '}
                      {queryInvoices?.data?.[INVOICES_STATES.REJECTED]
                        ?.length || 0}{' '}
                      )
                    </span>
                  </button>
                  <button
                    onClick={() => scrollToId(INVOICES_STATES.VOIDED)}
                    className="flex cursor-pointer items-center justify-between rounded-[12px] border border-portal-foreground p-3"
                  >
                    <span>Faturas anuladas</span>
                    <span className="text-portal-destructive">
                      ({' '}
                      {queryInvoices?.data?.[INVOICES_STATES.VOIDED]?.length ||
                        0}{' '}
                      )
                    </span>
                  </button>
                </div>
              </div>
            </div>
            {/* right-side */}
            <div className="col-span-8 mt-12 lg:ml-20 lg:mt-0">
              <div className="flex w-full flex-col gap-20">
                {/* Coupons */}
                <div id={INVOICES_STATES.APPROVED} className="flex flex-col">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <PortalSubHeading>Cupões gerados</PortalSubHeading>
                    {queryCoupons.isFetched &&
                      queryCoupons.data?.rows?.length > 0 && (
                        <>
                          {device === 'Mobile' || device === 'Tablet' ? (
                            <Button
                              variant="outline"
                              disabled={mutationCustomerCoupons.isPending}
                              onClick={async () => {
                                await mutationCustomerCoupons.mutateAsync();
                              }}
                            >
                              {mutationCustomerCoupons.isPending ? (
                                <Loader2Icon className="animate-spin" />
                              ) : (
                                <DownloadIcon />
                              )}
                              DownloadIcon de cupões
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              disabled={mutationCustomerCoupons.isPending}
                              onClick={async () => {
                                await mutationCustomerCoupons.mutateAsync();
                              }}
                            >
                              {mutationCustomerCoupons.isPending ? (
                                <Loader2Icon className="animate-spin" />
                              ) : (
                                <PrinterIcon />
                              )}
                              Imprimir cupões
                            </Button>
                          )}
                        </>
                      )}
                  </div>

                  <div className="mt-2 flex flex-col gap-4">
                    <DebouncedInput
                      type="number"
                      placeholder="Procurar por número do cupão"
                      onChange={(val) =>
                        table.getColumn('ROWID')?.setFilterValue(val)
                      }
                    />
                    {queryCoupons.isRefetching ? (
                      <div className="grid h-48 w-full place-items-center md:col-span-2 xl:col-span-3">
                        <Loading className="text-red-400" />
                      </div>
                    ) : table.getRowModel().rows?.length ? (
                      <>
                        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-4 3xl:grid-cols-4">
                          {table.getRowModel().rows.map((row) => (
                            <CardCoupon
                              key={row.id}
                              coupon={row.original.coupons}
                              invoice={row.original.invoices}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="col-span-3 flex flex-row items-center text-muted-foreground">
                        <CircleSlash2Icon className="mr-2 size-5" />
                        <h3>
                          {table.getState().columnFilters.length
                            ? `Não foram encontrados cupões com o número ${table.getState().columnFilters[0].value}`
                            : 'De momento não possui quaisquer Cupões.'}
                        </h3>
                      </div>
                    )}

                    <DataTableFooter
                      table={table}
                      rowsName="cupões"
                      pageSize={[8, 16, 24]}
                      hide={{ selectedRows: true, skipAll: true }}
                    />
                  </div>
                </div>

                {/* Pending */}
                <div id={INVOICES_STATES.PENDING} className="flex flex-col">
                  <PortalSubHeading className="text-amber-500">
                    Faturas pendentes
                  </PortalSubHeading>

                  {queryInvoices?.data?.[INVOICES_STATES.PENDING]?.length ? (
                    <span className="mt-2 text-portal-text">
                      Estes cupões encontram-se pendentes de validação. Caso
                      tenha efetuado o registo sem submeter comprovativo da
                      fatura, por favor desloque-se ao Balcão de Informações e
                      apresente o comprovativo original das respetivas faturas.
                    </span>
                  ) : null}

                  <div className="mt-4 flex flex-col gap-4">
                    {queryInvoices?.data?.[INVOICES_STATES.PENDING]?.length ? (
                      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 lg:gap-10 2xl:grid-cols-3">
                        {queryInvoices?.data?.[INVOICES_STATES.PENDING]?.map(
                          (item, index) => (
                            <CardInvoice
                              key={index}
                              invoice={item.invoices}
                              shop={item.shops}
                              minEntryAmount={campaign?.minEntryAmount}
                              className="border-amber-500"
                              pending={true}
                            />
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="col-span-3 flex flex-row items-center text-muted-foreground">
                        <CircleSlash2Icon className="mr-2 size-5" />
                        <h3>
                          De momento não possui quaisquer faturas pendentes
                        </h3>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rejected */}
                <div id={INVOICES_STATES.REJECTED} className="flex flex-col">
                  <PortalSubHeading className="text-portal-destructive">
                    Faturas rejeitadas
                  </PortalSubHeading>
                  <div className="mt-4 flex flex-col gap-4">
                    {queryInvoices?.data?.[INVOICES_STATES.REJECTED]?.length ? (
                      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 lg:gap-10 2xl:grid-cols-3">
                        {queryInvoices?.data?.[INVOICES_STATES.REJECTED]?.map(
                          (item, index) => (
                            <CardInvoice
                              key={index}
                              invoice={item.invoices}
                              shop={item.shops}
                              minEntryAmount={campaign?.minEntryAmount}
                              rejected={true}
                              className="border-portal-destructive"
                            />
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="col-span-3 flex flex-row items-center text-muted-foreground">
                        <CircleSlash2Icon className="mr-2 size-5" />
                        <h3>Não possui quaisquer faturas rejeitadas</h3>
                      </div>
                    )}
                  </div>
                </div>

                {/* Voided */}
                <div id={INVOICES_STATES.VOIDED} className="flex flex-col">
                  <PortalSubHeading className="text-portal-destructive">
                    Faturas anuladas pelo cliente
                  </PortalSubHeading>
                  <div className="mt-4 flex flex-col gap-4">
                    {queryInvoices?.data?.[INVOICES_STATES.VOIDED]?.length ? (
                      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 lg:gap-10 2xl:grid-cols-3">
                        {queryInvoices?.data?.[INVOICES_STATES.VOIDED]?.map(
                          (item, index) => (
                            <CardInvoice
                              key={index}
                              invoice={item.invoices}
                              shop={item.shops}
                              minEntryAmount={campaign?.minEntryAmount}
                              rejected={false}
                              className="border-portal-destructive"
                            />
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="col-span-3 flex flex-row items-center text-muted-foreground">
                        <CircleSlash2Icon className="mr-2 size-5" />
                        <h3>Não possui quaisquer faturas anuladas</h3>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PortalContainer>
    </CustomerAuthenticationWrapper>
  );
}
